<!-- ----------------------------------------------- -->
<template>
  <l-map
    :ref="mapObjName"
    :zoom="zoom"
    :min-zoom="minZoom"
    :max-zoom="maxZoom"
    :maxBounds="boundsArea"
    :center="center"
    :options="mapOptions"
    :preferCanvas="true"
    @update:zoom="updateZoom"
    @update:center="updateCenter"
    @update:bounds="updateCoord"
    @click="mapClick"
  >
    <l-tile-layer
      :url="mapReliefUrl"
      :opacity="0.3"
      :options="mapReliefOptions"
      v-if="mapType == 1"
      :zIndex="2"
    ></l-tile-layer>
    <l-tile-layer
      :url="mapBlankUrl"
      :options="mapBlankOptions"
      v-if="mapType == 3"
      :zIndex="2"
    ></l-tile-layer>
    <l-tile-layer
      :url="mapPaleUrl"
      :options="mapPaleOptions"
      v-if="mapType == 0 || mapType == 1 || mapType == 3"
      :zIndex="1"
    ></l-tile-layer>
    <l-tile-layer
      :url="mapSeamlessphotoUrl"
      v-if="mapType == 2"
      :options="mapSeamlessOptions"
      :zIndex="1"
    ></l-tile-layer>
    <point-layer></point-layer>
  </l-map>
</template>
<!-- ----------------------------------------------- -->
<script>
import { LMap, LTileLayer } from "vue2-leaflet";
import mixin from "../../lib/mixin.js";
import PointLayer from "../map/layer/PointLayer";

/**
 * 川の防災情報 地図画面
 * MapBase.vue
 * @author LIS
 */
export default {
  /** タグ名 */
  name: "kwb-map",
  mixins: [mixin],
  /** コンポーネント */
  components: {
    LMap,
    LTileLayer,
    PointLayer,
  },
  /** データ部 */
  data() {
    return {
      /** 地図オプション */
      mapOptions: {
        /** leafletのズームコントロール表示なし */
        zoomControl: false,
        /** 属性コントロール表示なし */
        attributionControl: false,
        /** ズームアニメーション閾値 */
        zoomAnimationThreshold: 2,
      },
      /** 地図タイル(淡色地図)の設定 */
      mapPaleOptions: {
        tms: false,
        maxNativeZoom: 18,
      },
      /** 地図タイル(標高地図)の設定 */
      mapReliefOptions: {
        tms: false,
        maxNativeZoom: 15,
        minNativeZoom: 5,
      },
      /** 地図タイル(航空写真)の設定 */
      mapSeamlessOptions: {
        tms: false,
        maxNativeZoom: 18,
      },
      /** 地図タイル(白地図)の設定 */
      mapBlankOptions: {
        tms: false,
        maxNativeZoom: 14,
        minNativeZoom: 5,
      },
      zoom: 0,
      center: [0, 0],
      mapObjName: "mapObj" + this.mapNum,
    };
  },
  props: {
    mapNum: {
      type: Number,
      default: 0,
    },
  },
  /** 初期化部 */
  mounted() {
    this.updateMapInfo(this.storeMapView.center, this.storeMapView.zoom);
    this.updateCoord(this.$refs[this.mapObjName].mapObject.getBounds());
  },
  /** 算出部 */
  computed: {
    /** 地図設定情報 */
    mapConfig: {
      get() {
        return this.$store.state.map.mapConfig;
      },
    },
    /** ストアのズームレベル・中心位置 */
    storeMapView: {
      get() {
        return {
          zoom: this.$store.state.map.zoom,
          center: this.$store.state.map.center,
        };
      },
    },
    /** 地図の最小ズームレベル */
    minZoom: {
      get() {
        return this.$store.state.map.minZoom;
      },
    },
    /** 地図の最大ズームレベル */
    maxZoom: {
      get() {
        return this.$store.state.map.maxZoom;
      },
    },
    /** 地図表示範囲 */
    boundsArea: {
      get() {
        return this.$store.state.map.boundsArea;
      },
    },
    /** 地図種別 */
    mapType: {
      get() {
        return this.$store.state.map.mapType;
      },
    },
    /** 地図タイル(淡色地図)のURL */
    mapPaleUrl: {
      get() {
        return this.$store.state.map.mapPaleUrl;
      },
    },
    /** 地図タイル(標高地図)のURL */
    mapReliefUrl: {
      get() {
        return this.$store.state.map.mapReliefUrl;
      },
    },
    /** 地図タイル(航空写真)のURL */
    mapSeamlessphotoUrl: {
      get() {
        return this.$store.state.map.mapSeamlessphotoUrl;
      },
    },
    /** 地図タイル(白地図)のURL */
    mapBlankUrl: {
      get() {
        return this.$store.state.map.mapBlankUrl;
      },
    },
    /** 地図左下・右上座標の取得 */
    minmaxCoord: {
      get() {
        return [this.$store.state.map.minCoord, this.$store.state.map.maxCoord];
      },
    },
    /** 地図移動アニメーション情報 */
    flyToInfo: {
      get() {
        return this.$store.state.map.flyToInfo;
      },
    },
    /** 地図移動情報 */
    moveInfo: {
      get() {
        return this.$store.state.map.moveInfo;
      },
    },
    /** 市町村座標リスト */
    townLatLngList: {
      get() {
        return this.$store.state.map.townLatLngList;
      },
    },
  },
  methods: {
    /** ズームレベル変更時処理 */
    updateZoom(level) {
      this.$store.commit("map/setViewZoom", level);
    },
    /** 中心座標更新時処理 */
    updateCenter(latLng) {
      this.$store.commit("map/setCenter", [latLng.lat, latLng.lng]);
    },
    /** 右上左下座標更新時処理 */
    updateCoord(latLngBounds) {
      // 地図番号が0(1枚表示か4枚表示の1枚目)のときのみ処理を行う
      if (this.mapNum == 0) {
        this.$store.commit("map/setCoord", latLngBounds);
      }
    },
    /** マップクリック時処理 */
    mapClick(e) {
      // 登録地編集モードの場合、登録地点選択情報を更新する
      if (this.$store.state.map.editLocationMode === 1) {
        this.$store.commit("map/setEditLocation", {
          zoom: this.$store.state.map.zoom,
          latlng: [e.latlng.lat, e.latlng.lng],
        });
      }
    },
    // 地図のズームレベル・中心位置を同時に更新
    updateMapInfo(center, zoom) {
      this.$refs[this.mapObjName].mapObject.setView(center, zoom);
    },
  },
  watch: {
    /** 地図移動を監視 */
    minmaxCoord() {
      // 地図番号が0(1枚表示か4枚表示の1枚目)のときのみ処理を行う
      if (this.mapNum == 0) {
        // URLパラメータ更新
        this.routerMapMove();
        // 表示市町村取得
        this.$store.dispatch("map/getTowns");

        // レーダの履歴動画再生状態をリセット
        if (this.$store.getters["radar/isStop"]) {
          this.$store.commit("radar/resetHistoryPlay");
        }
        // 観測所一覧ポップアップを閉じる
        this.$store.commit("map/setShowTmList", false);
      }
    },
    /** ストアのズームレベル・中心位置を監視 */
    storeMapView() {
      this.routerMapMove();
    },
    /** 地図移動情報を監視 */
    moveInfo() {
      this.updateMapInfo(this.moveInfo.center, this.moveInfo.zoom);
    },
    /** 地図移動アニメーション情報を監視 */
    flyToInfo() {
      this.$refs[this.mapObjName].mapObject.flyTo(
        this.flyToInfo.center,
        this.flyToInfo.zoom,
        { duration: 0.5 }
      );
    },
    /** 市町村座標リストを監視 */
    townLatLngList() {
      // 表示市町村リスト取得
      this.$store.dispatch("map/getTowns");
    },
  },
};
</script>
<!-- ----------------------------------------------- -->
<style lang="scss" scoped></style>
